import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LanguageProvider, useT } from "@/i18n/LanguageProvider";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";

// Below-the-fold sections load only after the hero paints, keeping the
// main bundle tiny (drops TBT by not parsing/executing this JS on load).
const About = lazy(() => import("@/components/About"));
const Services = lazy(() => import("@/components/Services"));
const Work = lazy(() => import("@/components/Work"));
const Process = lazy(() => import("@/components/Process"));
const Tools = lazy(() => import("@/components/Tools"));
const Contact = lazy(() => import("@/components/Contact"));
const Footer = lazy(() => import("@/components/Footer"));

// Cheap placeholder so lazy sections don't cause layout shifts if the user
// scrolls before the chunk arrives.
const SectionPlaceholder = () => (
  <div aria-hidden="true" style={{ minHeight: "60vh" }} />
);

const Home = () => {
  const { lang, transitioning } = useT();

  useEffect(() => {
    const el = document.getElementById("initial-loader");
    if (!el) return;
    const t1 = setTimeout(() => el.classList.add("done"), 250);
    const t2 = setTimeout(() => el.remove(), 1100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Prefetch below-the-fold section chunks once the browser is idle, so
  // scrolling stays smooth without adding to first-paint work.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const handle = idle(
      () => {
        import("@/components/About");
        import("@/components/Services");
        import("@/components/Work");
        import("@/components/Process");
        import("@/components/Tools");
        import("@/components/Contact");
        import("@/components/Footer");
      },
      { timeout: 3500 },
    );
    return () => {
      try {
        (window.cancelIdleCallback || clearTimeout)(handle);
      } catch (e) {
        /* noop */
      }
    };
  }, []);

  return (
    <div className="relative bg-[#050505] text-white overflow-x-hidden">
      <Nav />
      <AnimatePresence mode="wait">
        <motion.main
          key={lang}
          initial={{ opacity: 0 }}
          animate={{ opacity: transitioning ? 0.35 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          <Hero />
          <Suspense fallback={<SectionPlaceholder />}>
            <About />
          </Suspense>
          <Suspense fallback={<SectionPlaceholder />}>
            <Services />
          </Suspense>
          <Suspense fallback={<SectionPlaceholder />}>
            <Work />
          </Suspense>
          <Suspense fallback={<SectionPlaceholder />}>
            <Process />
          </Suspense>
          <Suspense fallback={<SectionPlaceholder />}>
            <Tools />
          </Suspense>
          <Suspense fallback={<SectionPlaceholder />}>
            <Contact />
          </Suspense>
        </motion.main>
      </AnimatePresence>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
          </Routes>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
